import { Box, IconButton, Typography } from '@material-ui/core';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowOutlinedIcon from '@material-ui/icons/PlayArrowOutlined';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import React, { useRef, useState } from 'react';
import { secondsToTime } from '../../utils/room-shared';
import { CustomVideoPlayerSlider } from './CustomVideoPlayerSlider';
import { CustomVolumeSlider } from './CustomVolumeSlider';
import { useStyles } from './VideoControl.styles';

type Props = {
  videoProgress: number;
  onSliderChange: (value: number) => void;

  onPlay: () => void;
  onPause: () => void;
  volume: number;
  isVideoPlaying: boolean;
  currentVideoTime: number;
  videoDuration: string;
  isFullScreen: boolean;
  isBuffering: boolean;
  isSmall?: boolean;
  bufferedProgress: number;
  onOpenFullScreen: () => void;
  onCloseFullScreen: () => void;
  onChangeVolume: (volume: number) => void;
  onStartScrub: () => void;
  onEndedScrub: () => void;
};
const VideoControl: React.FC<Props> = ({
  videoProgress,
  onSliderChange,
  volume,
  onChangeVolume,
  isVideoPlaying,
  currentVideoTime,
  videoDuration,
  isFullScreen,
  onOpenFullScreen,
  onCloseFullScreen,
  bufferedProgress,
  isSmall,
  onPlay,
  onPause,
  onEndedScrub,
  onStartScrub,
}) => {
  const classes = useStyles({ bufferedProgress: bufferedProgress });
  const [showVolumeSlider, setShowVolumeSlider] = useState<boolean>(false);
  const previousVolume = useRef<number>(1);
  const handleVolumeIconHover = () => {
    setShowVolumeSlider(!showVolumeSlider);
  };

  const iconFontSize = isSmall ? 'small' : 'large';
  const fontSize = isSmall ? 12 : 16;

  return (
    <Box className={classes.progressAction} onClick={(e) => e.stopPropagation()}>
      {/* <h1>Hello</h1> */}
      <Box paddingX={2} className={classes.progressBarContainer}>
        <CustomVideoPlayerSlider
          value={videoProgress}
          aria-labelledby="continuous-slider"
          onClick={(e) => e.stopPropagation()}
          onChange={(e, value) => {
            e.stopPropagation();
            onSliderChange(value as number);
          }}
          className={classes.progressBar}
          onMouseDown={onStartScrub}
          onMouseUp={onEndedScrub}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => {
            if (!isNaN(Number(videoDuration))) {
              const convertedSliderValue = (value * Number(videoDuration)) / 100;
              return secondsToTime(Math.floor(convertedSliderValue));
            }
            return value;
          }}
        />
      </Box>

      <Box paddingX={0.1} className={classes.bottomActions}>
        <div className={classes.controlsLeftContainer}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (!isVideoPlaying) {
                onPlay();
              } else {
                onPause();
              }
            }}
            size="small"
          >
            {!isVideoPlaying ? (
              <PlayArrowOutlinedIcon htmlColor="#fff" fontSize={iconFontSize} />
            ) : (
              <PauseIcon htmlColor="#fff" fontSize={iconFontSize} />
            )}
          </IconButton>
          <Typography variant="caption" style={{ color: '#fff', fontSize: fontSize }}>
            {`${secondsToTime(currentVideoTime)}/${secondsToTime(Number(videoDuration))}`}
          </Typography>
          {!isSmall && (
            <div
              className={classes.volumeWrapper}
              onMouseEnter={handleVolumeIconHover}
              onMouseLeave={handleVolumeIconHover}
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                size="small"
                onClick={() => {
                  onChangeVolume(volume === 0 ? previousVolume.current : 0);
                }}
              >
                {volume === 0 ? <VolumeOffIcon fontSize={iconFontSize} /> : <VolumeUpIcon fontSize={iconFontSize} />}
              </IconButton>

              {showVolumeSlider && (
                <div className={classes.volumeSliderWrapper}>
                  <CustomVolumeSlider
                    aria-labelledby="continuous-slider"
                    value={volume}
                    min={0}
                    max={1}
                    step={0.01}
                    onChange={(_, value) => {
                      if (showVolumeSlider) {
                        if (previousVolume.current) {
                          previousVolume.current = value as number;
                        }
                        onChangeVolume(value as number);
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <Box className={classes.controlsContainer}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (!isFullScreen) {
                onOpenFullScreen();
              } else {
                onCloseFullScreen();
              }
            }}
          >
            {!isFullScreen ? (
              <FullscreenIcon htmlColor="#fff" fontSize={iconFontSize} />
            ) : (
              <FullscreenExitIcon htmlColor="#fff" fontSize={iconFontSize} />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoControl;
